import { useState, type ReactNode } from 'react';

import { api } from '../api/client';
import {
  Badge,
  Button,
  Card,
  Field,
  Form,
  Loading,
  Notice,
  Reorderable,
  Select,
  Table,
} from '../components';
import {
  EMPTY_DRAFT,
  QuestionSettings,
  QuestionSummary,
  draftOf,
  type QuestionDraft,
  type QuestionNode,
} from './QuestionSettings';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * Forms and questions.
 *
 * Built after reading how SMA's admin did it, and shaped around the three things that made
 * that version painful to use:
 *
 *   1. **Children could only be added after saving.** SMA gated "+ Add Child Question" on
 *      edit mode, so building a branch meant save → navigate away → re-open → add. Here a
 *      child can be added the moment its parent's option exists.
 *   2. **A child was linked to the option's *text*.** Renaming a choice orphaned every
 *      question under it, silently. Here the link is the option's identity, so renaming
 *      "No" to "Not present" keeps its follow-ups.
 *   3. **Versions were the administrator's problem.** Here they are not mentioned: the
 *      service edits in place while nothing has been answered, and forks automatically once
 *      something has.
 *
 * The layout is a tree of nested cards, indented by depth, which is the shape the data has.
 *
 * **A follow-up hangs off one answer**, and only a Yes/No, Choose one or Choose several
 * question has answers to hang it from. Nesting goes as deep as the author needs, so long as
 * each level branches on a choice — which is the only shape where "asked when" has a
 * meaning. A follow-up asked regardless of the answer is not a branch, it is the next
 * question, and it read identically on screen to one that was conditional.
 */

const QUESTION_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Yes / No' },
  { value: 'SINGLE_CHOICE', label: 'Choose one' },
  { value: 'MULTI_CHOICE', label: 'Choose several' },
  { value: 'DATE', label: 'Date' },
  { value: 'PHOTO', label: 'Photograph' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'SIGNATURE', label: 'Signature' },
];

/** Types that carry choices, and can therefore have a follow-up per choice. */
const CHOICE_TYPES = new Set(['BOOLEAN', 'SINGLE_CHOICE', 'MULTI_CHOICE']);

/** Quick-add sets, because typing "Yes" and "No" by hand is work nobody should repeat. */
const OPTION_PRESETS: Array<{ label: string; options: string[] }> = [
  { label: 'Yes / No', options: ['Yes', 'No'] },
  { label: 'Good / Fair / Poor', options: ['Good', 'Fair', 'Poor'] },
  { label: 'Pass / Fail / Not applicable', options: ['Pass', 'Fail', 'Not applicable'] },
];

interface FormSummary {
  id: string;
  key: string;
  name: string;
  description: string | null;
  scope: string;
  displayOrder: number;
  isPublished: boolean;
  isActive: boolean;
  hasAnswers: boolean;
  questionCount: number;
  versionNumber: number;
  liveVersionNumber: number | null;
}

interface FormDetail extends FormSummary {
  sections: Array<{ id: string; name: string; displayOrder: number; questions: QuestionNode[] }>;
}

export function Forms(): JSX.Element {
  const { can, config } = useSession();
  const canEdit = can('workflow.edit');
  const forms = useResource(() => api.get<FormSummary[]>('/forms'), []);
  const { run, error, busy } = useAction();

  const [openId, setOpenId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const create = async (): Promise<void> => {
    const ok = await run(async () => {
      const created = await api.post<{ formId: string }>('/forms', { name: newName });
      setOpenId(created.formId);
    });
    if (ok) {
      setNewName('');
      forms.reload();
    }
  };

  /** The whole order in one call — what both the arrows and a drop produce. */
  const saveOrder = async (formIds: string[]): Promise<void> => {
    const ok = await run(async () => {
      await api.put('/forms/order', { formIds });
    });
    if (ok) forms.reload();
  };

  /** Swap a form with its neighbour and save the whole order. */
  const move = async (index: number, direction: -1 | 1): Promise<void> => {
    const list = [...(forms.data ?? [])];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;

    const a = list[index];
    const b = list[target];
    if (a === undefined || b === undefined) return;

    list[index] = b;
    list[target] = a;

    await saveOrder(list.map((form) => form.id));
  };

  if (openId !== null) {
    return (
      <FormEditor
        formId={openId}
        onBack={() => {
          setOpenId(null);
          forms.reload();
        }}
      />
    );
  }

  return (
    <>
      <Card title="Forms">
        <p className="muted">
          A form is a set of questions asked at a visit. The order below is the order a
          field user meets them in.
        </p>

        {error !== null && <Notice kind="error">{error}</Notice>}

        {forms.loading ? (
          <Loading />
        ) : forms.error !== null ? (
          <Notice kind="error">{forms.error}</Notice>
        ) : (
          <Table
            rows={forms.data ?? []}
            keyOf={(row) => row.id}
            empty="No forms yet. Create one below."
            onRowClick={(row) => setOpenId(row.id)}
            onReorder={(ordered) => void saveOrder(ordered.map((form) => form.id))}
            columns={[
              {
                header: 'Order',
                render: (row) => {
                  const index = (forms.data ?? []).findIndex((form) => form.id === row.id);
                  return (
                    <div className="row">
                      <button
                        className="btn btn-secondary"
                        disabled={busy || index === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          void move(index, -1);
                        }}
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={busy || index === (forms.data ?? []).length - 1}
                        onClick={(event) => {
                          event.stopPropagation();
                          void move(index, 1);
                        }}
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  );
                },
              },
              { header: 'Form', render: (row) => row.name },
              { header: 'Questions', numeric: true, render: (row) => row.questionCount },
              {
                header: 'Asked',
                // "area" was written literally here while the deployment calls them zones.
                render: (row) =>
                  row.scope === 'SITE'
                    ? `Once per ${config.siteLabel.toLowerCase()}`
                    : `Once per ${config.subUnitLabel.toLowerCase()}`,
              },
              {
                header: 'Status',
                render: (row) => (
                  <div className="row">
                    <Badge tone={row.isPublished ? 'accepted' : 'flagged'}>
                      {row.isPublished ? 'Live' : 'Draft'}
                    </Badge>
                    {/* A form can be live *and* have unpublished edits waiting. Shown here
                        so the list does not read as "Draft" for something field users are
                        using every day. */}
                    {!row.isPublished && row.liveVersionNumber !== null && (
                      <Badge tone="accepted">v{row.liveVersionNumber} still live</Badge>
                    )}
                    {/* Distinct from Draft: a form can be published and still withdrawn. */}
                    {!row.isActive && <Badge tone="flagged">Not collected</Badge>}
                    {row.hasAnswers && <Badge tone="low">Has answers</Badge>}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/*
        Every editing control on this screen is gated on `workflow.edit`.

        The nav shows Forms to anyone holding `workflow.view`, and this page had no
        permission checks at all — so a store manager, who holds the view permission and not
        the edit one, was given the whole editor: Edit, Delete, the answer presets, drag to
        reorder, Make live. The server refused each one with a 403, correctly, but only
        after the click. An action that is always going to fail should not be offered.
      */}
      {canEdit && (
      <Card title="Add a form">
        <Form onSubmit={() => void create()}>
          <div className="form-row">
            <Field
              label="Form name"
              value={newName}
              onChange={setNewName}
              required
              hint="e.g. Monthly safety check. Questions are added next."
            />
          </div>
          <div className="form-actions">
            <Button type="submit" disabled={busy || newName === ''}>
              {busy ? 'Creating…' : 'Create and add questions'}
            </Button>
          </div>
        </Form>
      </Card>
      )}
    </>
  );
}

function FormEditor({ formId, onBack }: { formId: string; onBack: () => void }): JSX.Element {
  const canEdit = useSession().can('workflow.edit');
  const form = useResource(() => api.get<FormDetail>(`/forms/${formId}`), [formId]);
  const { run, error, busy } = useAction();

  /**
   * Which "add a follow-up" form is open.
   *
   * One key for the whole tree, so only one is ever open — `option:<id>` for a branch under
   * a particular answer, `question:<id>` for one asked whenever the question is answered.
   * Prefixed rather than relying on ids never colliding across two tables.
   */
  const [addingUnder, setAddingUnder] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  /** One PUT with the whole order, which is what the service takes. */
  const saveQuestionOrder = async (questionIds: string[]): Promise<void> => {
    const ok = await run(async () => {
      await api.put('/questions/order', { questionIds });
    });
    if (ok) form.reload();
  };

  const publish = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/forms/${formId}/publish`);
    });
    if (ok) form.reload();
  };

  /**
   * Every section, not the first one.
   *
   * This read `sections[0]`, so a form with more than one section showed only the questions
   * in its first — and the ones below it could not be seen or edited here at all. They were
   * still asked in the field, which is the worst shape for this to take: a question nobody
   * could find in the panel arriving on a phone, with no way to change or remove it.
   *
   * The count in the forms list came from the API and counted all of them, so the header
   * said nine questions above an editor showing seven. The screen was contradicting itself
   * and that was the only visible symptom.
   */
  const sections = form.data?.sections ?? [];

  return (
    <>
      <Card
        title={form.data?.name ?? 'Loading…'}
        actions={
          <>
            <Button variant="secondary" onClick={onBack}>
              Back to forms
            </Button>
            {canEdit && (
              <Button variant="secondary" onClick={() => setShowSettings(!showSettings)}>
                {showSettings ? 'Hide settings' : 'Form settings'}
              </Button>
            )}
            {canEdit && form.data !== null && !form.data.isPublished && (
              <Button onClick={() => void publish()} disabled={busy || form.data.questionCount === 0}>
                Make live
              </Button>
            )}
          </>
        }
      >
        {error !== null && <Notice kind="error">{error}</Notice>}

        {form.data !== null && form.data.hasAnswers && (
          <Notice kind="info">
            Visits have already been recorded against this form. Your changes are saved to a
            new version automatically — the answers already collected keep meaning what they
            meant when they were given.
          </Notice>
        )}

        {/*
          The one thing the screen used not to say. Editing a live form forks a draft, so a
          question added here is saved and correct but is *not* what field users are being
          given — and without this, the only evidence was a "Make live" button appearing.
          The obvious conclusion was that the change had not saved at all.
        */}
        {form.data !== null &&
          !form.data.isPublished &&
          form.data.liveVersionNumber !== null && (
            <Notice kind="info">
              These changes are saved but not live yet. Field users are still being given
              version {form.data.liveVersionNumber}; this is version {form.data.versionNumber}.
              Choose <strong>Make live</strong> when the form is ready.
            </Notice>
          )}

        <p className="muted">
          Add a question, then add follow-ups under any of its answers. A follow-up is asked
          only when that answer is chosen, and can have follow-ups of its own — so nesting
          goes as deep as you need. Only Yes/No, Choose one and Choose several questions have
          answers to attach one to.
        </p>
      </Card>

      {showSettings && form.data !== null && (
        <FormSettings form={form.data} onSaved={form.reload} />
      )}

      {form.loading ? (
        <Loading />
      ) : form.error !== null ? (
        <Notice kind="error">{form.error}</Notice>
      ) : sections.length === 0 ? (
        <Notice kind="info">This form has no section to hold questions.</Notice>
      ) : (
        <>
          {sections.map((group) => (
            <div key={group.id}>
              {/*
                Named only when there is more than one. A lone section is the form itself,
                and heading it would add a level to read that carries no information.
              */}
              {sections.length > 1 && <h3 className="section-heading">{group.name}</h3>}

              {group.questions.length === 0 ? (
                <p className="empty">No questions in this section yet.</p>
              ) : (
                <Reorderable
                  items={group.questions}
                  keyOf={(question) => question.id}
                  onReorder={(ordered) => void saveQuestionOrder(ordered.map((entry) => entry.id))}
                  disabled={!canEdit}
                >
                  {(question, index, handle) => (
                    <QuestionCard
                      question={question}
                      formId={formId}
                      depth={0}
                      siblings={group.questions}
                      index={index}
                      handle={handle}
                      addingUnder={addingUnder}
                      setAddingUnder={setAddingUnder}
                      onChanged={form.reload}
                    />
                  )}
                </Reorderable>
              )}
            </div>
          ))}

          {canEdit && (
            <>
              <Card title="Add a question">
                <AddQuestionForm formId={formId} onAdded={form.reload} />
              </Card>

              <Card title="Reuse a question">
                <ReuseQuestion formId={formId} onAdded={form.reload} />
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}


/**
 * The form as a thing: what it is called, where it applies, whether it is in use.
 *
 * None of this is versioned. A name and a scope describe the questionnaire rather than the
 * questions it asked, so changing them cannot alter what a recorded answer meant — forking a
 * version for a rename would only litter the history.
 */
function FormSettings({ form, onSaved }: { form: FormDetail; onSaved: () => void }): JSX.Element {
  const { config } = useSession();
  // `/sites` is paginated — `{ items, total, … }`, not a bare array. A default page would
  // silently scope the form to whatever fitted on page one, so the size is set explicitly.
  const sites = useResource(
    () => api.get<{ items: SiteRow[] }>('/sites?pageSize=500'),
    [],
  );
  const assigned = useResource(() => api.get<string[]>(`/forms/${form.id}/sites`), [form.id]);
  const roles = useResource(() => api.get<RoleRow[]>('/roles'), []);
  const assignedRoles = useResource(() => api.get<string[]>(`/forms/${form.id}/roles`), [form.id]);
  const { run, error, busy } = useAction();

  const [name, setName] = useState(form.name);
  const [perZone, setPerZone] = useState(form.scope === 'SUB_UNIT');
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [chosenRoles, setChosenRoles] = useState<string[] | null>(null);

  // Loaded once, then owned locally so ticking a box does not need a round trip.
  const current = chosen ?? assigned.data ?? [];
  const currentRoles = chosenRoles ?? assignedRoles.data ?? [];

  const save = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.patch(`/forms/${form.id}`, {
        name,
        scope: perZone ? 'SUB_UNIT' : 'SITE',
      });
      await api.put(`/forms/${form.id}/sites`, { siteIds: current });
      await api.put(`/forms/${form.id}/roles`, { roleIds: currentRoles });
    });
    if (ok) {
      setChosen(null);
      setChosenRoles(null);
      assigned.reload();
      assignedRoles.reload();
      onSaved();
    }
  };

  const setActive = async (isActive: boolean): Promise<void> => {
    const ok = await run(async () => {
      await api.patch(`/forms/${form.id}`, { isActive });
    });
    if (ok) onSaved();
  };

  return (
    <Card title="Form settings">
      {error !== null && <Notice kind="error">{error}</Notice>}

      <Form onSubmit={() => void save()}>
        <div className="form-row">
          <Field label="Form name" value={name} onChange={setName} required />
        </div>

        {/*
          Asked as a question rather than offered as a "scope" dropdown, because the choice is
          a yes/no about how the visit runs: one set of answers for the place, or one set per
          zone. The consequence is spelled out because it is not reversible in the answers
          already collected — turning it on does not retrospectively split them.
        */}
        <h3>How often it is asked</h3>
        <label className="row">
          <input
            type="checkbox"
            checked={perZone}
            onChange={() => setPerZone(!perZone)}
          />
          <span>
            Ask this form once for every {config.subUnitLabel.toLowerCase()}
          </span>
        </label>
        <p className="muted">
          {perZone
            ? `Each ${config.subUnitLabel.toLowerCase()} at a ${config.siteLabel.toLowerCase()} is answered separately, so one visit collects one set of answers per ${config.subUnitLabel.toLowerCase()}. A ${config.siteLabel.toLowerCase()} with no ${config.subUnitLabel.toLowerCase()}s is still asked the form once.`
            : `Answered once for the whole ${config.siteLabel.toLowerCase()}.`}{' '}
          Answers already recorded are unchanged — they keep meaning what they meant.
        </p>

        <h3>Where it applies</h3>
        <p className="muted">
          Tick nothing to ask this form at every {config.siteLabel.toLowerCase()}. Tick some
          and it is asked only there.
        </p>

        {sites.loading || assigned.loading ? (
          <Loading />
        ) : (
          <div className="stack">
            {(sites.data?.items ?? []).map((site) => (
              <label key={site.id} className="row">
                <input
                  type="checkbox"
                  checked={current.includes(site.id)}
                  onChange={() =>
                    setChosen(
                      current.includes(site.id)
                        ? current.filter((id) => id !== site.id)
                        : [...current, site.id],
                    )
                  }
                />
                <span>
                  {site.name} <span className="muted">{site.code}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Said in both directions. The line used to appear only while nothing was ticked, so
            saving a selection left the author with ticks and no statement of what they mean —
            and "nothing ticked means everywhere" is exactly the rule worth restating. */}
        <p className="muted">
          {current.length === 0
            ? `Currently asked everywhere — ${(sites.data?.items ?? []).length} ${config.siteLabel.toLowerCase()}(s).`
            : `Currently asked at ${current.length} ${config.siteLabel.toLowerCase()}(s) only.`}
        </p>

        <h3>Who is asked it</h3>
        <p className="muted">
          Tick nothing and every {config.fieldUserLabel.toLowerCase()} is asked this form.
          Tick some roles and only they are.
        </p>

        {roles.loading || assignedRoles.loading ? (
          <Loading />
        ) : (
          <div className="stack">
            {(roles.data ?? []).map((role) => (
              <label key={role.id} className="row">
                <input
                  type="checkbox"
                  checked={currentRoles.includes(role.id)}
                  onChange={() =>
                    setChosenRoles(
                      currentRoles.includes(role.id)
                        ? currentRoles.filter((id) => id !== role.id)
                        : [...currentRoles, role.id],
                    )
                  }
                />
                <span>{role.name}</span>
              </label>
            ))}
          </div>
        )}

        <p className="muted">
          {currentRoles.length === 0
            ? 'Currently asked of every role.'
            : `Currently asked only of: ${(roles.data ?? [])
                .filter((role) => currentRoles.includes(role.id))
                .map((role) => role.name)
                .join(', ')}.`}
        </p>

        <div className="form-actions">
          <Button type="submit" disabled={busy || name === ''}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Form>

      <h3>In use</h3>
      {/* Deactivating is not deleting, and the difference is worth saying: the visits already
          recorded against this form keep their answers and their verdicts. */}
      <p className="muted">
        {form.isActive
          ? 'This form is collected on visits. Taking it out of use stops that; nothing already recorded changes.'
          : 'This form is not collected on any visit. Visits recorded before it was withdrawn are unchanged.'}
      </p>
      <Button
        variant={form.isActive ? 'danger' : 'primary'}
        disabled={busy}
        onClick={() => void setActive(!form.isActive)}
      >
        {form.isActive ? 'Take out of use' : 'Put back into use'}
      </Button>
    </Card>
  );
}

interface SiteRow {
  id: string;
  code: string;
  name: string;
}

interface RoleRow {
  id: string;
  key: string;
  name: string;
}

function QuestionCard({
  question,
  formId,
  depth,
  siblings,
  index,
  handle,
  addingUnder,
  setAddingUnder,
  onChanged,
}: {
  question: QuestionNode;
  formId: string;
  depth: number;
  siblings: QuestionNode[];
  index: number;
  /** Rendered only where a drag surface supplied one; the arrows work everywhere. */
  handle?: ReactNode;
  addingUnder: string | null;
  setAddingUnder: (value: string | null) => void;
  onChanged: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const canEdit = useSession().can('workflow.edit');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<QuestionDraft>(() => draftOf(question));
  const [optionLabel, setOptionLabel] = useState('');

  const takesOptions = CHOICE_TYPES.has(question.stepType);

  const save = async (): Promise<void> => {
    const ok = await run(async () => {
      // Everything the type actually uses, and nothing it does not — sending a camera
      // setting on a Text question would store a value the app never reads and an author
      // would later wonder why changing it did nothing.
      await api.patch(`/questions/${question.id}`, {
        prompt: draft.prompt,
        helpText: draft.helpText,
        isRequired: draft.isRequired,
        requiresEvidence: draft.requiresEvidence,
        ...(question.stepType === 'PHOTO' ? { cameraFacing: draft.cameraFacing } : {}),
        ...(question.stepType === 'DATE'
          ? {
              dateMode: draft.dateMode,
              minDateMode: draft.minDateMode,
              maxDateMode: draft.maxDateMode,
              minDate: draft.minDate === '' ? null : draft.minDate,
              maxDate: draft.maxDate === '' ? null : draft.maxDate,
            }
          : {}),
        ...(question.stepType === 'NUMBER'
          ? {
              minValue: draft.minValue === '' ? null : Number(draft.minValue),
              maxValue: draft.maxValue === '' ? null : Number(draft.maxValue),
            }
          : {}),
      });
    });
    if (ok) {
      setEditing(false);
      onChanged();
    }
  };

  const remove = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.delete(`/questions/${question.id}`);
    });
    if (ok) onChanged();
  };

  const addOption = async (label: string): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/questions/${question.id}/options`, { label });
    });
    if (ok) {
      setOptionLabel('');
      onChanged();
    }
  };

  const applyPreset = async (labels: string[]): Promise<void> => {
    const ok = await run(async () => {
      for (const label of labels) {
        await api.post(`/questions/${question.id}/options`, { label });
      }
    });
    if (ok) onChanged();
  };

  /** Mark, or unmark, an answer as one somebody needs to look at. */
  const setEscalates = async (optionId: string, escalates: boolean): Promise<void> => {
    const ok = await run(async () => {
      await api.patch(`/options/${optionId}`, { escalates });
    });
    if (ok) onChanged();
  };

  const removeOption = async (optionId: string): Promise<void> => {
    const ok = await run(async () => {
      await api.delete(`/options/${optionId}`);
    });
    if (ok) onChanged();
  };

  const move = async (direction: -1 | 1): Promise<void> => {
    const list = [...siblings];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;

    const a = list[index];
    const b = list[target];
    if (a === undefined || b === undefined) return;

    list[index] = b;
    list[target] = a;

    const ok = await run(async () => {
      await api.put('/questions/order', { questionIds: list.map((entry) => entry.id) });
    });
    if (ok) onChanged();
  };

  return (
    // Depth as indentation. The tree is the data's real shape, so showing it flat would
    // hide the one thing an author needs to check: which answer reveals what.
    <div style={{ marginLeft: depth === 0 ? 0 : 22 }}>
      <Card
        title={undefined}
        actions={undefined}
      >
        {error !== null && <Notice kind="error">{error}</Notice>}

        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="row">
            {/* The depth, said out loud. Indentation alone stops being readable about three
                levels in, which is exactly where an author most needs to know where they
                are. */}
            {depth > 0 && <Badge tone="low">Follow-up · level {depth + 1}</Badge>}
            <Badge tone="low">{QUESTION_TYPES.find((t) => t.value === question.stepType)?.label ?? question.stepType}</Badge>
            {question.isRequired && <Badge tone="flagged">Required</Badge>}
            {question.requiresEvidence && <Badge tone="flagged">Needs a photo</Badge>}
          </div>

          {canEdit && (
            <div className="row">
              {handle}
              {/* Ordering works on any set of siblings — the service just writes displayOrder
                  by position — so there is no reason to withhold it below the top level.
                  The arrows stay alongside the drag handle: native drag is mouse-only. */}
              <Button variant="secondary" onClick={() => void move(-1)} disabled={busy || index === 0}>
                ↑
              </Button>
              <Button
                variant="secondary"
                onClick={() => void move(1)}
                disabled={busy || index === siblings.length - 1}
              >
                ↓
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setDraft(draftOf(question));
                  setEditing(!editing);
                }}
              >
                {editing ? 'Cancel' : 'Edit'}
              </Button>
              <Button variant="danger" onClick={() => void remove()} disabled={busy}>
                Delete
              </Button>
            </div>
          )}
        </div>

        {editing ? (
          <Form onSubmit={() => void save()}>
            <QuestionSettings
              stepType={question.stepType}
              draft={draft}
              onChange={setDraft}
              promptLabel="Question"
            />
            <div className="form-actions">
              <Button type="submit" disabled={busy || draft.prompt === ''}>
                Save
              </Button>
            </div>
          </Form>
        ) : (
          <>
            <h3 style={{ marginTop: 8 }}>{question.prompt}</h3>
            {question.helpText !== null && question.helpText !== '' && (
              <p className="muted">{question.helpText}</p>
            )}
            <QuestionSummary question={question} />
          </>
        )}

        {takesOptions && (
          <div className="stack" style={{ marginTop: 12 }}>
            {question.options.length === 0 && (
              <>
                {/* A choice question with no choices cannot be answered. Said plainly,
                    because SMA allowed exactly this and the result only showed up on a
                    field user's phone. */}
                <Notice kind="error">
                  This question offers no answers yet. Add at least two.
                </Notice>
                {/* The warning above stays for everyone — a reader should be able to see
                    that a form is broken. Only the buttons that repair it are withheld. */}
                {canEdit && (
                  <div className="row">
                    {OPTION_PRESETS.map((preset) => (
                      <Button
                        key={preset.label}
                        variant="secondary"
                        onClick={() => void applyPreset(preset.options)}
                        disabled={busy}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                )}
              </>
            )}

            {question.options.map((option) => (
              <div key={option.id} className="option-block">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div className="row">
                    <strong>{option.label}</strong>
                    {option.escalates && <Badge tone="flagged">Escalates</Badge>}
                  </div>
                  {canEdit && (
                    <div className="row">
                      {/* Report-only, and said so: an author ticking this should not think they
                          are stopping the visit. The gate blocks; this raises a flag. */}
                      <label className="row" title="Choosing this answer puts the visit on the escalations list. It does not block the visit.">
                        <input
                          type="checkbox"
                          checked={option.escalates}
                          disabled={busy}
                          onChange={() => void setEscalates(option.id, !option.escalates)}
                        />
                        <span>Escalate</span>
                      </label>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setAddingUnder(addingUnder === `option:${option.id}` ? null : `option:${option.id}`)
                        }
                      >
                        {addingUnder === `option:${option.id}` ? 'Cancel' : '+ Follow-up question'}
                      </Button>
                      <Button variant="danger" onClick={() => void removeOption(option.id)} disabled={busy}>
                        Remove answer
                      </Button>
                    </div>
                  )}
                </div>

                {option.children.length > 0 && (
                  <p className="muted">
                    Asked only when “{option.label}” is chosen:
                  </p>
                )}

                {option.children.map((child, childIndex) => (
                  <QuestionCard
                    key={child.id}
                    question={child}
                    formId={formId}
                    depth={depth + 1}
                    siblings={option.children}
                    index={childIndex}
                    addingUnder={addingUnder}
                    setAddingUnder={setAddingUnder}
                    onChanged={onChanged}
                  />
                ))}

                {addingUnder === `option:${option.id}` && (
                  <div style={{ marginLeft: 22 }}>
                    <Card>
                      <AddQuestionForm
                        formId={formId}
                        parentStepId={question.id}
                        parentOptionId={option.id}
                        onAdded={() => {
                          setAddingUnder(null);
                          onChanged();
                        }}
                      />
                    </Card>
                  </div>
                )}
              </div>
            ))}

            {canEdit && (
              <Form onSubmit={() => void addOption(optionLabel)}>
                <div className="form-row">
                  <Field label="Add an answer" value={optionLabel} onChange={setOptionLabel} />
                </div>
                <div className="form-actions">
                  <Button type="submit" disabled={busy || optionLabel === ''}>
                    Add answer
                  </Button>
                </div>
              </Form>
            )}
          </div>
        )}

        {/*
          Follow-ups attached to the question rather than to one of its answers — asked
          whenever it is answered at all.

          This branch is the reason nesting looked shallow. Both clients rendered it from
          the start, but nothing created it: the only "+ Follow-up question" button lived
          inside an answer, so a Text, Number or Photograph question — which has no answers
          to branch from — could never be given a follow-up at all. The button below is that
          missing half, and it is offered on every question regardless of type.
        */}
        {/*
          Follow-ups of the older shape — attached to the question rather than to one of its
          answers. They can no longer be created: "asked whenever this is answered" is not a
          branch, and it let a follow-up hang off a Text or Photograph question where there
          is no answer for it to depend on. Still rendered, so nothing already authored
          disappears from the screen.
        */}
        {question.children.length > 0 && (
          <div className="stack" style={{ marginTop: 12 }}>
            <p className="muted">
              Asked whenever this question is answered — an older form of follow-up. Move it
              under a specific answer, or delete it.
            </p>
            {question.children.map((child, childIndex) => (
              <QuestionCard
                key={child.id}
                question={child}
                formId={formId}
                depth={depth + 1}
                siblings={question.children}
                index={childIndex}
                addingUnder={addingUnder}
                setAddingUnder={setAddingUnder}
                onChanged={onChanged}
              />
            ))}
          </div>
        )}

        {/* Said where an author will look for the button, rather than left as an absence. */}
        {!takesOptions && (
          <p className="muted" style={{ marginTop: 12 }}>
            Follow-ups hang off an answer, so only Yes/No, Choose one and Choose several can
            have them.
          </p>
        )}
      </Card>
    </div>
  );
}


/**
 * Two ways to not retype a question.
 *
 * Both **copy**. Neither leaves a live link back to where it came from — a central edit that
 * reached into published forms would rewrite the question an already-recorded answer was
 * given to, which is exactly what versioning exists to prevent. Said here as well as in the
 * service, because "library" strongly implies the opposite.
 */
function ReuseQuestion({ formId, onAdded }: { formId: string; onAdded: () => void }): JSX.Element {
  const library = useResource(() => api.get<LibraryNode[]>('/library/questions'), []);
  const forms = useResource(() => api.get<FormSummary[]>('/forms'), []);
  const { run, error, busy } = useAction();

  const [mode, setMode] = useState<'LIBRARY' | 'FORM'>('LIBRARY');
  const [sourceFormId, setSourceFormId] = useState('');
  const source = useResource(
    async () => (sourceFormId === '' ? null : api.get<FormDetail>(`/forms/${sourceFormId}`)),
    [sourceFormId],
  );

  const insert = async (libraryQuestionId: string): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/forms/${formId}/questions/from-library`, { libraryQuestionId });
    });
    if (ok) onAdded();
  };

  const copy = async (sourceQuestionId: string): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/forms/${formId}/questions/copy`, { sourceQuestionId });
    });
    if (ok) onAdded();
  };

  const otherForms = (forms.data ?? []).filter((form) => form.id !== formId);

  return (
    <>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <div className="row" style={{ marginBottom: 12 }}>
        <Button
          variant={mode === 'LIBRARY' ? 'primary' : 'secondary'}
          onClick={() => setMode('LIBRARY')}
        >
          From the library
        </Button>
        <Button variant={mode === 'FORM' ? 'primary' : 'secondary'} onClick={() => setMode('FORM')}>
          From another form
        </Button>
      </div>

      <p className="muted">
        A copy is taken, with its follow-ups. Editing the original later does not change this
        form, and editing this form does not change the original.
      </p>

      {mode === 'LIBRARY' ? (
        library.loading ? (
          <Loading />
        ) : (library.data ?? []).length === 0 ? (
          <Notice kind="info">
            The library is empty. Add questions to it under <strong>Question library</strong>.
          </Notice>
        ) : (
          <Table
            rows={library.data ?? []}
            keyOf={(row) => row.id}
            columns={[
              { header: 'Category', render: (row) => row.category ?? '—' },
              { header: 'Question', render: (row) => row.prompt },
              {
                header: 'Type',
                render: (row) =>
                  QUESTION_TYPES.find((type) => type.value === row.stepType)?.label ?? row.stepType,
              },
              {
                header: 'Follow-ups',
                numeric: true,
                render: (row) =>
                  row.options.reduce((total, option) => total + option.children.length, 0),
              },
              {
                header: '',
                render: (row) => (
                  <Button variant="secondary" disabled={busy} onClick={() => void insert(row.id)}>
                    Insert
                  </Button>
                ),
              },
            ]}
          />
        )
      ) : (
        <>
          <div className="form-row">
            <Select
              label="Copy from"
              value={sourceFormId}
              onChange={setSourceFormId}
              options={[
                { value: '', label: 'Choose a form…' },
                ...otherForms.map((form) => ({ value: form.id, label: form.name })),
              ]}
            />
          </div>

          {source.loading && sourceFormId !== '' && <Loading />}

          {source.data !== null && source.data !== undefined && (
            <Table
              rows={source.data.sections.flatMap((section) => section.questions)}
              keyOf={(row) => row.id}
              empty="That form has no questions yet."
              columns={[
                { header: 'Question', render: (row) => row.prompt },
                {
                  header: 'Type',
                  render: (row) =>
                    QUESTION_TYPES.find((type) => type.value === row.stepType)?.label ??
                    row.stepType,
                },
                {
                  header: 'Follow-ups',
                  numeric: true,
                  render: (row) =>
                    row.options.reduce((total, option) => total + option.children.length, 0),
                },
                {
                  header: '',
                  render: (row) => (
                    <Button variant="secondary" disabled={busy} onClick={() => void copy(row.id)}>
                      Copy
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </>
      )}
    </>
  );
}

interface LibraryNode {
  id: string;
  category: string | null;
  prompt: string;
  stepType: string;
  options: Array<{ id: string; label: string; children: LibraryNode[] }>;
}

function AddQuestionForm({
  formId,
  parentStepId,
  parentOptionId,
  onAdded,
}: {
  formId: string;
  parentStepId?: string;
  parentOptionId?: string;
  onAdded: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [stepType, setStepType] = useState('TEXT');
  const [draft, setDraft] = useState<QuestionDraft>(EMPTY_DRAFT);

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/forms/${formId}/questions`, {
        prompt: draft.prompt,
        stepType,
        ...(draft.helpText === '' ? {} : { helpText: draft.helpText }),
        isRequired: draft.isRequired,
        requiresEvidence: draft.requiresEvidence,
        ...(stepType === 'PHOTO' ? { cameraFacing: draft.cameraFacing } : {}),
        ...(stepType === 'DATE'
          ? {
              dateMode: draft.dateMode,
              minDateMode: draft.minDateMode,
              maxDateMode: draft.maxDateMode,
              ...(draft.minDate === '' ? {} : { minDate: draft.minDate }),
              ...(draft.maxDate === '' ? {} : { maxDate: draft.maxDate }),
            }
          : {}),
        ...(stepType === 'NUMBER'
          ? {
              ...(draft.minValue === '' ? {} : { minValue: Number(draft.minValue) }),
              ...(draft.maxValue === '' ? {} : { maxValue: Number(draft.maxValue) }),
            }
          : {}),
        ...(parentStepId === undefined ? {} : { parentStepId }),
        ...(parentOptionId === undefined ? {} : { parentOptionId }),
      });
    });

    if (ok) {
      setDraft(EMPTY_DRAFT);
      setStepType('TEXT');
      onAdded();
    }
  };

  return (
    <>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Select
            label="Answer type"
            value={stepType}
            onChange={setStepType}
            required
            options={QUESTION_TYPES}
          />
        </div>

        <QuestionSettings
          stepType={stepType}
          draft={draft}
          onChange={setDraft}
          promptLabel={parentOptionId === undefined ? 'Question' : 'Follow-up question'}
        />

        {CHOICE_TYPES.has(stepType) && stepType !== 'BOOLEAN' && (
          <p className="muted">Answers are added once the question exists.</p>
        )}

        <div className="form-actions">
          <Button type="submit" disabled={busy || draft.prompt === ''}>
            {busy ? 'Adding…' : 'Add question'}
          </Button>
        </div>
      </Form>
    </>
  );
}
